import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface DetailRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Set on the last row of a card so it does not draw a trailing separator. */
  noBorder?: boolean;
  /** Top-align the value, for rows holding multi-line text. */
  alignTop?: boolean;
}

/** A single "label / value" line inside a {@link DetailCard}. */
export const DetailRow = ({ label, value, noBorder = false, alignTop = false }: DetailRowProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '260px 1fr' },
      gap: { xs: 0.5, sm: 2 },
      alignItems: 'start',
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? 'none' : '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="body2" component="div" sx={{ color: 'text.secondary', fontWeight: 600 }}>
      {label}
    </Typography>
    <Box
      sx={{
        minHeight: '24px',
        display: 'flex',
        alignItems: alignTop ? 'flex-start' : 'center',
        minWidth: 0,
      }}
    >
      {value}
    </Box>
  </Box>
);

/** The bordered container the rows sit in. */
export const DetailCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      overflow: 'hidden',
      bgcolor: 'background.paper',
    }}
  >
    {children}
  </Box>
);

interface DetailSectionProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Rendered on the section header line, e.g. an edit button. */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/** A titled group of rows: heading, optional description, then one card. */
export const DetailSection = ({ title, description, action, children }: DetailSectionProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {title ? (
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        ) : (
          <span />
        )}
        {action}
      </Box>
    )}
    {description && (
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    )}
    <DetailCard>{children}</DetailCard>
  </Box>
);

/** The plain text style used for a row's value. */
export const DetailValue = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ wordBreak: 'break-word', minWidth: 0 }}>
    {children}
  </Typography>
);

/** A hover hint next to a row label, for rules that need a sentence to explain. */
export const InfoHint = ({ text }: { text: string }) => (
  <Tooltip title={text}>
    <InfoOutlinedIcon
      fontSize="small"
      sx={{ color: 'text.secondary', cursor: 'help', ml: 0.5, verticalAlign: 'middle' }}
    />
  </Tooltip>
);
